import { Badge } from './ui/Badge'
import { runStatusLook, type RunStatus } from '../lib/runStatus'
import { cn } from '../lib/utils'

/**
 * Run status, colour-coded. The mapping and the reasoning behind each colour live
 * in `lib/runStatus` — the run rail needs a dot for the same seven values, and two
 * copies of the table would drift.
 *
 * The badge shows the raw status, not the look's `label`: this sits beside a run id
 * in a header, where the value as the API wrote it is what a reader would quote in
 * a bug report. The rail uses the sentence-case label instead, and the label rides
 * along here as the `title` — a supplement, never the only place a word appears.
 *
 * **The dot is the same `look.dot` the rail draws**, inside the badge, so the two
 * surfaces read as one vocabulary rather than as a coloured pill and an unrelated
 * coloured dot. It also carries the whole signal in the one case where the badge
 * carries none: `RunSummary['status']` ends in `| string`, so a status this UI has
 * not been taught is a real path, and `runStatusLook` answers it with a neutral
 * look and the raw value. Never a fallback to `succeeded` — that would render a
 * sweep whose data sources stopped answering as a finished one.
 */
export function StatusBadge({ status }: { status: RunStatus }) {
  const look = runStatusLook(status)
  return (
    <Badge variant={look.badge} title={look.label}>
      <span
        aria-hidden="true"
        className={cn('h-1.5 w-1.5 shrink-0 rounded-full', look.dot)}
      />
      {status}
    </Badge>
  )
}

/**
 * Tier, which is the ranker's bucketing of promotion-worthiness — not a rank.
 *
 * Two vocabularies map to the same four colours. `context/target-criteria` §7
 * names the bands immediate / conditional / exploratory / archive, and that is
 * what a current run writes; A/B/C/D is retained because rows from before it was
 * renamed are still on the board. Anything unrecognised falls through to neutral
 * rather than being coerced into a colour that implies a judgement.
 *
 * `TIER_VARIANTS` is the one home of tier colour in this UI. Anything that needs a
 * tier's colour renders `TierBadge`; nothing gets a second copy of the table.
 */
const TIER_VARIANTS: Record<string, 'green' | 'blue' | 'yellow' | 'red'> = {
  immediate: 'green',
  a: 'green',
  conditional: 'blue',
  b: 'blue',
  exploratory: 'yellow',
  c: 'yellow',
  archive: 'red',
  d: 'red',
}

export function TierBadge({ tier }: { tier: string | null }) {
  // In words, because an em dash in a tier column is a value a reader has to
  // interpret and the two readings are opposite: "this was assessed and placed
  // nowhere" against "nothing has placed it". A board row can legitimately carry
  // no tier, and that is the second one.
  if (!tier) return <span className="text-2xs text-slate-500">Not scored</span>
  return (
    <Badge variant={TIER_VARIANTS[tier.trim().toLowerCase()] ?? 'default'}>
      {tier}
    </Badge>
  )
}
